import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Users, ChevronDown, ChevronRight, Code, Play, Loader2, Copy, Check, FileSpreadsheet } from 'lucide-react';
import { format } from 'date-fns';

interface User {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  createdAt: string;
  lastLoginTimestamp: string | null;
  marketingConsent: boolean;
  isActive: boolean;
}

interface ImportedContact {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  matchedUserId?: number | null;
  validationErrors?: string[] | null;
}

interface UserCriteriaViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  criteriaId: number;
  criteriaName: string;
  criteriaType?: string;
}

export function UserCriteriaViewDialog({
  open,
  onOpenChange,
  criteriaId,
  criteriaName,
  criteriaType,
}: UserCriteriaViewDialogProps) {
  const isBulkImport = criteriaType === 'bulk_import';
  const [showQuery, setShowQuery] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const [copied, setCopied] = useState(false);

  // Fetch matching users
  const { data, isLoading, error, refetch } = useQuery<{
    users: User[];
    count: number;
    sqlQuery: string;
    evaluatedAt: string;
    contacts?: ImportedContact[];
  }>({
    queryKey: ['/api/admin/crm/criteria', criteriaId, 'users'],
    enabled: open,
  });

  const handleCopyQuery = async () => {
    if (data?.sqlQuery) {
      await navigator.clipboard.writeText(data.sqlQuery);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleTestQuery = async () => {
    if (!data?.sqlQuery) return;
    
    setIsTesting(true);
    try {
      const response = await fetch('/api/admin/crm/criteria/test-query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: data.sqlQuery }),
      });
      const result = await response.json();
      setTestResults(result);
    } catch (err) {
      console.error('Error testing query:', err);
      setTestResults({ error: 'Failed to execute query' });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {criteriaName}
          </DialogTitle>
          <DialogDescription>
            {isBulkImport
              ? 'View imported contacts for this bulk import criteria.'
              : 'View all users matching this criteria and the SQL query used to fetch them'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Stats */}
          <div className="flex items-center gap-4">
            {isBulkImport ? (
              <>
                <Badge variant="secondary" className="text-sm">
                  <FileSpreadsheet className="h-3 w-3 mr-1" />
                  {(data?.contacts?.length ?? 0)} imported contacts
                </Badge>
              </>
            ) : (
              <>
                <Badge variant="secondary" className="text-sm">
                  <Users className="h-3 w-3 mr-1" />
                  {data?.count || 0} matching users
                </Badge>
                {data?.contacts && data.contacts.length > 0 && (
                  <Badge variant="secondary" className="text-sm">
                    <FileSpreadsheet className="h-3 w-3 mr-1" />
                    {data.contacts.length} imported contacts
                  </Badge>
                )}
              </>
            )}
            {data?.evaluatedAt && (
              <span className="text-xs text-muted-foreground">
                Last evaluated: {format(new Date(data.evaluatedAt), 'PPp')}
              </span>
            )}
          </div>

          {!isBulkImport && (
            <>
              {/* SQL Query Section */}
              <Collapsible open={showQuery} onOpenChange={setShowQuery}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full justify-between" data-testid="button-toggle-query">
                    <div className="flex items-center gap-2">
                      <Code className="h-4 w-4" />
                      <span>SQL Query</span>
                    </div>
                    {showQuery ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-2">
                  <div className="relative">
                    <pre className="p-4 bg-muted rounded-md overflow-x-auto text-sm">
                      <code data-testid="text-sql-query">{data?.sqlQuery}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2"
                      onClick={handleCopyQuery}
                      data-testid="button-copy-query"
                    >
                      {copied ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleTestQuery}
                      disabled={isTesting}
                      data-testid="button-test-query"
                    >
                      {isTesting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      Test Query
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => refetch()}
                      data-testid="button-refresh-results"
                    >
                      Refresh Results
                    </Button>
                  </div>
                  {testResults && (
                    <div className="p-3 bg-muted rounded-md">
                      <p className="text-sm font-medium mb-2">Test Results:</p>
                      <pre className="text-xs overflow-x-auto" data-testid="text-test-results">
                        {JSON.stringify(testResults, null, 2)}
                      </pre>
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>

              {/* Users Table */}
              <div className="border rounded-md">
                <ScrollArea className="h-[400px]">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-40">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : error ? (
                    <div className="flex items-center justify-center h-40 text-destructive">
                      Error loading users
                    </div>
                  ) : data?.users && data.users.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ID</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Registered</TableHead>
                          <TableHead>Last Login</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.users.map((user) => (
                          <TableRow key={user.id} data-testid={`row-user-${user.id}`}>
                            <TableCell className="font-mono text-xs" data-testid={`text-user-id-${user.id}`}>
                              {user.id}
                            </TableCell>
                            <TableCell data-testid={`text-user-email-${user.id}`}>
                              {user.email}
                            </TableCell>
                            <TableCell data-testid={`text-user-name-${user.id}`}>
                              {user.firstName || user.lastName
                                ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                                : '-'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {format(new Date(user.createdAt), 'PP')}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {user.lastLoginTimestamp
                                ? format(new Date(user.lastLoginTimestamp), 'PP')
                                : 'Never'}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {user.isActive && (
                                  <Badge variant="outline" className="text-xs">
                                    Active
                                  </Badge>
                                )}
                                {user.marketingConsent && (
                                  <Badge variant="secondary" className="text-xs">
                                    Consented
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="flex items-center justify-center h-40 text-muted-foreground">
                      No users match this criteria
                    </div>
                  )}
                </ScrollArea>
              </div>
            </>
          )}

          {data?.contacts && data.contacts.length > 0 && (
            <div className="border rounded-md">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div>
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    Imported Contacts
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Showing first 200 contacts from the latest bulk import.
                  </p>
                </div>
              </div>
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Matched User</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.contacts.slice(0, 200).map((contact, index) => {
                      const errors = Array.isArray(contact.validationErrors)
                        ? contact.validationErrors
                        : [];
                      return (
                        <TableRow key={`${contact.email}-${index}`}>
                          <TableCell className="text-sm">{contact.email}</TableCell>
                          <TableCell className="text-sm">
                            {contact.firstName} {contact.lastName}
                          </TableCell>
                          <TableCell className="text-sm">{contact.phone || '—'}</TableCell>
                          <TableCell className="text-sm">
                            {contact.matchedUserId ? `User #${contact.matchedUserId}` : 'Unmatched'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {errors.length ? (
                              <Badge variant="destructive" className="text-xs">
                                {errors[0]}
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Valid</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
              {data.contacts.length > 200 && (
                <p className="text-xs text-muted-foreground px-4 py-2">
                  Showing first 200 contacts. Use the bulk import manager to download the full list.
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
